import { Component, OnInit, Input } from '@angular/core';
import { PagingFilter } from '@admin/shared/models/paging-filter.model';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { MatchingAnnouncementsFilterModel } from '../shared/models/matching-announcements-filter-model';
import { SelectItem, LazyLoadEvent } from 'primeng/api';
import { AnnouncementMatchingService } from '../shared/services/announcement-matching.service';
import { AnnouncementMatchingModel } from '../shared/models/announcement-matching-model';
import { AnnouncementMatchingListApprovalStatus } from '../shared/models/announcement-matching-list-approval-status.enum';
import { UserService } from '@admin/core/user.service';
import { FeatureEnum } from '@admin/shared/models/feature-enum';
import { MessageType } from '@admin/core/models/enum/message-type';
import { ApplicationMessageSource } from '@admin/core/models/application-message-source';
import { ApplicationMessage } from '@admin/core/models/application-message';
import { GenericResult } from '@admin/shared/models/generic-result';
import { CustomMessageService } from '@admin/core/message.service';
import { Table } from 'primeng/table';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-matching-announcements-list',
  templateUrl: './matching-announcements-list.component.html',
  styleUrls: ['./matching-announcements-list.component.less'],
  standalone: false,
})
export class MatchingAnnouncementsListComponent implements OnInit {
  public filterPagingWrapper: PagingFilter<MatchingAnnouncementsFilterModel>;
  public pageSize: number;
  public totalRecords: number;
  public lastLoadEvent: LazyLoadEvent;
  public isReset: boolean;

  public matchedAnnouncements: AnnouncementMatchingModel[];

  public announcementIdFilter: number;
  public portfolioIdFilter: number;
  public announcementTitleFilter: string;
  public companyNameFilter: string;
  public approvalStatusIdFilter: number;

  public announcementMatchingListApprovalStatus =
    AnnouncementMatchingListApprovalStatus;
  public dateFormat: string;

  @Input()
  public display: boolean;
  public displayModal: boolean;

  public cols: any[];
  public statuses: SelectItem[];
  public portfolios: SelectItem[];

  public hasViewMatchingListPermission: boolean;
  public hasApproveMatchingListPermission: boolean;
  public hasExpressApprovePermission: boolean;

  constructor(
    private config: ConfigurationService,
    private translationService: TranslateService,
    private messageService: CustomMessageService,
    private userService: UserService,
    private announcementMatchingService: AnnouncementMatchingService
  ) {
    this.filterPagingWrapper = new PagingFilter(
      new MatchingAnnouncementsFilterModel()
    );
    this.totalRecords = 0;

    this.objInit();

    this.hasViewMatchingListPermission = false;
    this.hasApproveMatchingListPermission = false;
    this.hasExpressApprovePermission = false;

    this.matchedAnnouncements = [];
    this.cols = [
      { field: 'portfolio', header: 'Portfolio' },
      { field: 'publishDate', header: 'PublishDate' },
      { field: 'announcementTitle', header: 'AnnouncementTitle' },
      { field: 'companyName', header: 'Company' },
      { field: 'status', header: 'Status' },
      { field: 'matches', header: 'Matches' },
      { field: 'accepted', header: 'ToSend' },
      { field: 'rejected', header: 'Rejected' },
      { field: 'ambiguous', header: '?' },
      { field: 'approveAndSend', header: 'ApproveAndSend' },
    ];
    this.statuses = [
      {
        label: this.translationService.instant('label.AllStatuses'),
        value: null,
      },
      {
        label: this.translationService.instant('label.NotApproved'),
        value: AnnouncementMatchingListApprovalStatus.NotApproved,
      },
      {
        label: this.translationService.instant('label.Approved'),
        value: AnnouncementMatchingListApprovalStatus.Approved,
      },
      {
        label: this.translationService.instant('label.Rejected'),
        value: AnnouncementMatchingListApprovalStatus.Rejected,
      },
    ];
    this.portfolios = [];
  }

  ngOnInit() {
    let config = this.config.getCachedConfig();
    this.dateFormat = config.dateFormat;
    this.pageSize = config.defaultPageSize;

    const userFeatures = this.userService.currentUser.features;
    this.hasViewMatchingListPermission = userFeatures.some(
      (f) => f === FeatureEnum.ViewMatchingList
    );
    this.hasApproveMatchingListPermission = userFeatures.some(
      (f) => f === FeatureEnum.ApproveMatchingList
    );
    this.hasExpressApprovePermission = userFeatures.some(
      (f) => f === FeatureEnum.ExpressApprove
    );
  }

  private objInit() {
    this.announcementIdFilter = null;
    this.portfolioIdFilter = null;
    this.announcementTitleFilter = null;
    this.companyNameFilter = null;
    this.approvalStatusIdFilter = null;
  }

  private portfoliosDropdownInit() {
    let portfolioIds: number[] = [];
    this.portfolios.push({
      label: this.translationService.instant('label.AllPortfolios'),
      value: null,
    });

    this.matchedAnnouncements.forEach((ma) => {
      let entry: SelectItem = { label: ma.portfolio, value: ma.portfolioId };

      if (!portfolioIds.includes(entry.value)) {
        portfolioIds.push(entry.value);
        this.portfolios.push(entry);
      }
    });
  }

  public onListClick(dt: Table) {
    this.objInit();
    dt.reset();
    this.onFilter();
  }

  public onLoad(event: LazyLoadEvent) {
    this.filterPagingWrapper.pagingInfo.start = event.first;
    this.filterPagingWrapper.pagingInfo.limit = event.rows;
    this.lastLoadEvent = event;

    let filters: { [index: string]: any } = [];

    if (event.filters != null) {
      for (let f in event.filters) {
        {
          filters[f] = event.filters[f].value;
        }
      }
    }

    this.filterPagingWrapper.filter.portfolioId = <number>filters['portfolio'];
    this.filterPagingWrapper.filter.announcementId = <number>(
      filters['announcementId']
    );
    this.filterPagingWrapper.filter.announcementTitle =
      filters['announcementTitle'];
    this.filterPagingWrapper.filter.companyName = filters['companyName'];
    this.filterPagingWrapper.filter.approvalStatusId = <number>(
      filters['status']
    );

    this.onFilter();
  }

  public onFilter() {
    this.filterPagingWrapper.pagingInfo.start =
      this.filterPagingWrapper.pagingInfo.start || 0;
    this.announcementMatchingService
      .listAnnouncementMatching(this.filterPagingWrapper)
      .subscribe((matching) => {
        this.totalRecords = matching.totalCount;
        this.matchedAnnouncements = matching.items;

        if (this.portfolios.length == 0) this.portfoliosDropdownInit();
      });
  }

  public onGenerate(display: boolean) {
    this.displayModal = display;
  }

  public onClose(display: boolean) {
    this.displayModal = display;
  }

  public onUpdateTable(announcementId: number) {
    this.filterPagingWrapper.filter.announcementId = announcementId;
    this.onFilter();
  }

  public onApprove(announcementMatchingListId: number) {
    this.announcementMatchingService
      .ExpressApprove(announcementMatchingListId)
      .subscribe((d) => {
        this.expressApproveResult(d);
      });
  }

  private expressApproveResult(result: GenericResult) {
    if (result.success) {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Success,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant(
          'message.ExpressApproveSuccessful'
        ),
      });
    } else {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant('message.' + result.error),
      });
    }
  }
}
